import { PartnerCommissionStatus } from '@/@types/PartnerProgram';
import { usePermissions } from '@/auth/PermissionsContext';
import CardCurrency from '@/components/shared/CardCurrency/CardCurrency';
import StatusBadge from '@/components/shared/Badge/Badge';
import Divider from '@/components/shared/Divider/Divider';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrencyFromCents } from '@/lib/select-options';
import { PartnerLinksService } from '@/services/partner-links.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WithdrawalForm from '@/pages/Financial/Partials/WithdrawalForm';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function AffiliateReferralsTab() {
  const { t } = useTranslation(['reports', 'affiliates', 'common', 'wallet']);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const { modals, openModal, closeModal } = useModalStore();
  const { hasPermission, isPrimary } = usePermissions();
  const currency = useCurrencyStore((s) => s.currency);

  const canRequestPartnerWithdrawal =
    isPrimary &&
    hasPermission('partner_indications.request_partner_commission_withdrawal');

  const { data: wallet, isLoading: isLoadingWallet } = useQuery({
    queryKey: ['partner-wallet', currency],
    queryFn: () => PartnerLinksService.getPartnerWallet(currency),
    staleTime: 30 * 1000,
  });

  const { data: commissions, isLoading: isLoadingCommissions } = useQuery({
    queryKey: ['partner-commissions', page, statusFilter, currency],
    queryFn: () =>
      PartnerLinksService.getPartnerCommissions({
        page,
        per_page: 15,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        currency,
      }),
    staleTime: 15 * 1000,
  });

  const { data: affiliatesData, isLoading: isLoadingAffiliates } = useQuery({
    queryKey: ['partner-affiliates', currency],
    queryFn: () =>
      PartnerLinksService.getPartnerAffiliates({
        only_active: true,
        per_page: 50,
        currency,
      }),
    staleTime: 30 * 1000,
  });

  const { data: partnerProductsData } = useQuery({
    queryKey: ['partner-products'],
    queryFn: () => PartnerLinksService.getPartnerProducts({ per_page: 100 }),
    staleTime: 60 * 1000,
  });

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    affiliatesData?.data?.forEach((aff) => {
      if (aff.product?.id && aff.product?.name) {
        map.set(aff.product.id, aff.product.name);
      }
    });
    partnerProductsData?.data?.forEach((product) => {
      if (product.id && product.name) {
        map.set(product.id, product.name);
      }
    });
    return map;
  }, [affiliatesData, partnerProductsData]);

  const hasWithdrawableBalance =
    Number(wallet?.withdrawal?.available_balance_withdrawal ?? 0) > 0;
  const isWithdrawalDisabled = isLoadingWallet || !hasWithdrawableBalance;

  const statusBadgeMap: Record<PartnerCommissionStatus, string> = {
    pending: 'pending',
    available: 'active',
    paid: 'completed',
    canceled: 'disabled',
  };

  const headers = [
    { title: t('affiliates:affiliate'), key: 'affiliate' },
    { title: t('common:product'), key: 'product' },
    { title: t('affiliates:amount'), key: 'amount' },
    { title: t('common:status'), key: 'status' },
    { title: t('common:date'), key: 'date' },
  ];

  const getProductLabel = (productId: string, productObj?: { id: string; name: string }) => {
    if (productObj?.name) return productObj.name;
    const name = productNameById.get(productId);
    return name ?? t('common:product');
  };

  return (
    <>
      {canRequestPartnerWithdrawal && (
        <CustomSheet
          open={modals['withdrawal-form-partner']}
          onClose={() => closeModal('withdrawal-form-partner')}
        >
          <WithdrawalForm
            key={`withdrawal-partner-${currency}`}
            enabled={Boolean(modals['withdrawal-form-partner'])}
            availableBalance={wallet?.withdrawal?.available_balance_withdrawal_formatted ?? ''}
            availableBalanceCents={Math.round(
              Number(wallet?.withdrawal?.available_balance_withdrawal ?? 0) * 100,
            )}
            currency={currency}
            type="partner"
            onClose={() => closeModal('withdrawal-form-partner')}
          />
        </CustomSheet>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <CardCurrency
          text={t('affiliates:available_commission_balance')}
          value={wallet?.withdrawal?.available_formatted ?? ''}
          isLoading={isLoadingWallet}
          status="paid"
          button={
            canRequestPartnerWithdrawal ? (
              isWithdrawalDisabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex max-xl:w-full">
                      <Button className="max-xl:w-full" disabled>
                        {t('common:request_withdrawal')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('wallet:withdrawal_validation.no_balance')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  className="max-xl:w-full"
                  onClick={() => openModal('withdrawal-form-partner')}
                >
                  {t('common:request_withdrawal')}
                </Button>
              )
            ) : undefined
          }
        />
        <CardCurrency
          text={t('affiliates:total_commission_withdrawn')}
          value={wallet?.withdrawal?.withdrawal_formatted ?? ''}
          isLoading={isLoadingWallet}
          status="period"
        />
      </div>

      <div className="mt-8">
        <h2 className="text-base font-semibold mb-4">
          {t('affiliates:affiliated_affiliates', 'Afiliados Indicados')}
        </h2>
        {isLoadingAffiliates ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4 flex flex-col gap-4 animate-pulse h-32" />
            ))}
          </div>
        ) : !affiliatesData?.data || affiliatesData.data.length === 0 ? (
          <Card className="p-8 flex flex-col items-center justify-center text-center">
            <Users className="h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">
              {t('affiliates:no_affiliated_affiliates_title', 'Nenhum afiliado indicado')}
            </h3>
            <p className="text-muted-foreground mt-2 max-w-sm">
              {t('affiliates:no_affiliated_affiliates_desc', 'Compartilhe seus links de convite para começar a receber indicações.')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {affiliatesData.data.map((aff) => (
              <Card key={aff.id} className="p-5 flex flex-col gap-4 border border-zinc-800 bg-[#18181B]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-10 w-10 border border-zinc-800 bg-zinc-900 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium text-xs">
                        {aff.affiliate?.name?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-sm text-zinc-100 truncate">
                        {aff.affiliate?.name}
                      </span>
                      <span className="text-xs text-zinc-400 truncate">
                        {aff.affiliate?.email}
                      </span>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-zinc-900 border-zinc-800 text-zinc-300 shrink-0 text-[10px]">
                    {aff.accepted_at ? new Date(aff.accepted_at).toLocaleDateString() : '-'}
                  </Badge>
                </div>
                
                <div className="h-[1px] w-full bg-zinc-800" />
                
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider mb-1.5">
                      {t('common:product')}
                    </span>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6 border border-zinc-800 bg-zinc-900 shrink-0">
                        <AvatarImage src={aff.product?.image} className="object-cover" />
                        <AvatarFallback className="bg-zinc-800 text-zinc-400 text-[8px] font-bold">
                          {getProductLabel(aff.product.id, aff.product)?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-zinc-300 truncate">
                        {getProductLabel(aff.product.id, aff.product)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end shrink-0">
                    <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider mb-1.5">
                      {t('affiliates:commissions_count', 'Comissões')}
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-base font-bold text-primary">
                        {formatCurrencyFromCents(aff.metrics?.commissions_total_converted ?? 0, currency)}
                      </span>
                      <span className="text-xs text-zinc-500 font-medium">
                        ({aff.metrics?.commissions_count ?? 0})
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card className="mt-8 overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold">
              {t('affiliates:commission_statement', 'Extrato de Comissões')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('affiliates:sales_statement_description', 'Acompanhe as comissões geradas pelos seus afiliados indicados.')}
            </p>
          </div>
          <div className="w-full md:w-48">
            <CustomSelect
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={[
                { value: 'all', label: t('common:all_status') },
                { value: 'pending', label: t('reports:pending') },
                { value: 'available', label: t('reports:available') },
                { value: 'paid', label: t('reports:paid') },
                { value: 'canceled', label: t('reports:canceled') },
              ]}
              placeholder={t('common:select_status')}
            />
          </div>
        </div>

        <TableListCustom
          headers={headers}
          isLoadingData={isLoadingCommissions}
          data={commissions ?? { data: [] }}
          emptyIconMessage={<DollarSign />}
          emptyTitleMessage={t('reports:no_data_title')}
          emptyDescriptionMessage={t('reports:no_data_description')}
          currentPage={page}
          setCurrentPage={setPage}
        >
          {commissions?.data?.map((c) => (
            <TableRow key={c.id} className="h-16 border-b border-border">
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {c.affiliate?.name}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8 bg-blackA1">
                    <AvatarImage 
                      src={c.product?.image} 
                      className="h-full w-full object-cover"
                      alt={getProductLabel(c.product_id, c.product)}
                      loading="lazy"
                    />
                    <AvatarFallback className="bg-elevated text-xs font-medium text-muted">
                      {getProductLabel(c.product_id, c.product)?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-muted-foreground">
                    {getProductLabel(c.product_id, c.product)}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <span className="font-semibold text-foreground">
                  {formatCurrencyFromCents(c.amount_converted || 0, c.currency_converted || 'BRL')}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge
                  status={statusBadgeMap[c.status] as any}
                  text={t(`reports:${c.status}`)}
                />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {c.available_at ? new Date(c.available_at).toLocaleDateString() : c.paid_at ? new Date(c.paid_at).toLocaleDateString() : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableListCustom>
      </Card>
    </>
  );
}
